import React, { useState, useEffect } from 'react';
import { Box, Typography, Link, Grow } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  WarningAmber as WarningAmberIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';

interface BetaDisclaimerProps {
  animate?: boolean;
}

const BetaDisclaimer: React.FC<BetaDisclaimerProps> = ({ animate = false }) => {
  const [show1, setShow1] = useState(!animate);
  const [show2, setShow2] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    const t1 = setTimeout(() => setShow1(true), 100);
    const t2 = setTimeout(() => setShow2(true), 2100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [animate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, sm: 4 },
        alignItems: 'flex-start',
      }}
    >
      {/* Beta disclaimer */}
      <Grow in={show1} timeout={600} style={{ transformOrigin: '0 0 0' }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <WarningAmberIcon
            sx={{
              fontSize: 28,
              color: 'warning.main',
              flexShrink: 0,
              filter: (theme) =>
                `drop-shadow(0 0 7px ${alpha(theme.palette.warning.main, 0.32)})`,
            }}
          />
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                display: 'block',
                mb: 0.75,
                letterSpacing: '0.04em',
              }}
            >
              Early Beta — Back up your work
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1.7 }}
            >
              Studio Vault is in active development. Always keep independent
              backups of your projects and do not rely on Studio Vault as your
              only backup. We are not liable for data loss during this period.
            </Typography>
          </Box>
        </Box>
      </Grow>

      {/* Community */}
      <Grow in={show2} timeout={600} style={{ transformOrigin: '0 0 0' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <ForumIcon
            sx={{
              fontSize: 28,
              color: 'warning.dark',
              flexShrink: 0,
              filter: (theme) =>
                `drop-shadow(0 0 7px ${alpha(theme.palette.warning.dark, 0.28)})`,
            }}
          />
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                display: 'block',
                mb: 0.75,
                letterSpacing: '0.04em',
              }}
            >
              Community &amp; Feedback
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1.7 }}
            >
              Questions, bugs, or ideas? This beta is free and open to everyone,
              so please join our community and share your feedback! We read
              everything. The more information you can provide, the faster we
              can fix issues and build the features you want.{' '}
              <Link
                href="https://discord.gg/srd3rH6WYU"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'warning.main',
                  fontWeight: 700,
                  textDecorationColor: (theme) =>
                    alpha(theme.palette.warning.main, 0.72),
                  textUnderlineOffset: '3px',
                  '&:hover': {
                    color: 'warning.light',
                    textDecorationColor: 'warning.light',
                  },
                }}
              >
                Join the Discord
              </Link>
            </Typography>
          </Box>
        </Box>
      </Grow>

      <Box
        component="nav"
        aria-label="Legal"
        sx={{ display: 'flex', gap: 2, fontSize: '12px' }}
      >
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{
            color: 'text.secondary',
            textUnderlineOffset: '3px',
            '&:hover': { color: 'text.primary' },
            '&:focus-visible': {
              outline: (theme) =>
                `2px solid ${alpha(theme.palette.warning.main, 0.8)}`,
              outlineOffset: 3,
            },
          }}
        >
          Privacy
        </Link>
        <Link
          component={RouterLink}
          to="/terms"
          sx={{
            color: 'text.secondary',
            textUnderlineOffset: '3px',
            '&:hover': { color: 'text.primary' },
            '&:focus-visible': {
              outline: (theme) =>
                `2px solid ${alpha(theme.palette.warning.main, 0.8)}`,
              outlineOffset: 3,
            },
          }}
        >
          Terms
        </Link>
      </Box>
    </Box>
  );
};

export default BetaDisclaimer;
